import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import Header from "../components/Layout/Header";
import WorkflowBoard from "../components/Workflow/WorkflowBoard";
import { configurationsAPI } from "../services/api";

const WorkflowBoardPage = () => {
  const navigate = useNavigate();
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfiguration, setSelectedConfiguration] = useState(() => {
    const saved = localStorage.getItem("selectedConfiguration");
    return saved ? parseInt(saved, 10) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadConfigurations();
  }, []);

  useEffect(() => {
    if (configurations.length > 0) {
      const savedConfigId = localStorage.getItem("selectedConfiguration");
      const savedConfigIdNum = savedConfigId
        ? parseInt(savedConfigId, 10)
        : null;

      const savedConfigExists =
        savedConfigIdNum &&
        configurations.some((c) => c.id === savedConfigIdNum);

      if (savedConfigExists) {
        setSelectedConfiguration(savedConfigIdNum);
      } else if (!selectedConfiguration) {
        const firstConfigId = configurations[0].id;
        setSelectedConfiguration(firstConfigId);
        localStorage.setItem("selectedConfiguration", firstConfigId.toString());
      }
    }
  }, [configurations]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await configurationsAPI.getAll();
      console.log("WorkflowBoardPage: Loaded configurations:", response.data);
      const configs = response.data || [];
      setConfigurations(configs);
      if (configs.length === 0) {
        setError(
          "No configurations found. Please create a configuration first.",
        );
      }
    } catch (err) {
      console.error("WorkflowBoardPage: Failed to load configurations:", err);
      setError(err.response?.data?.error || "Failed to load configurations");
      setConfigurations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigurationChange = (event) => {
    const configId = parseInt(event.target.value, 10);
    setSelectedConfiguration(configId);
    localStorage.setItem("selectedConfiguration", configId.toString());
  };

  if (loading) {
    return (
      <Box>
        <Header />
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5" component="h1">
              Change Request Workflow Board
            </Typography>
            <FormControl variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel>Configuration</InputLabel>
              <Select
                value={selectedConfiguration || ""}
                onChange={handleConfigurationChange}
                label="Configuration"
              >
                {configurations.map((config) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {selectedConfiguration ? (
          <WorkflowBoard activeConfigurationId={selectedConfiguration} />
        ) : (
          <Paper sx={{ p: 3 }}>
            <Alert severity="info">
              Please select a configuration to view the workflow board.
            </Alert>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default WorkflowBoardPage;
